import React, { useState } from "react";
import {
  Box,
  Card,
  CardBody,
  Flex,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Skeleton,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { DataModelStats } from "build-ts/interface";
import { useUser } from "context/UserContext";
import { AppIcons } from "constants/icons";
import ConfigurationListFilterControl from "modules/configuration/components/ConfigurationListFilterControl";
import { epochConverterWithTimeZone } from "utils/dateUtils";
import { HeaderWithStats } from "services/MetadataServices";

interface ModelListProps {
  listModel: HeaderWithStats[];
  handleOpenModelEditor: (id: string) => void;
  onOpenThoughtspotFormula: (modelId: string) => void;
  onDeleteModel: (id: string) => void;
  statsLoading: boolean;
}

/** 32–36px ring: gray empty, amber partial, green full. */
function MappingProgressRing({
  tooltipLabel,
  size = 34,
  strokeWidth = 6,
  progressPct,
}: {
  tooltipLabel: string;
  size?: number;
  strokeWidth?: number;
  progressPct: number;
}) {
  const r = (size - strokeWidth) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  const dashOffset = circumference - (progressPct / 100) * circumference;
  const progressStroke = progressPct === 100 ? "#38A169" : progressPct > 0 ? "#D69E2E" : "#A0AEC0";

  return (
    <Tooltip label={tooltipLabel} hasArrow openDelay={300} bg="gray.700" color="white" fontSize="xs">
      <Box position="relative" w={`${size}px`} h={`${size}px`} flexShrink={0} aria-label={tooltipLabel}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }} aria-hidden>
          <circle cx={c} cy={c} r={r} fill="none" stroke="#E2E8F0" strokeWidth={strokeWidth} />
          <circle
            cx={c}
            cy={c}
            r={r}
            fill="none"
            stroke={progressStroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={dashOffset}
            style={{ transition: "stroke-dashoffset 0.25s ease, stroke 0.2s ease" }}
          />
        </svg>
      </Box>
    </Tooltip>
  );
}

function CompactStatusChip({ icon, value, ariaLabel }: { icon: React.ElementType; value: number; ariaLabel: string }) {
  return (
    <HStack
      spacing={1}
      as="span"
      align="center"
      py={1}
      px={2}
      borderRadius="md"
      borderWidth="1px"
      borderColor="gray.200"
      bg="gray.50"
      flexShrink={0}
      cursor="default"
      color="gray.400"
      _hover={{ color: "gray.700", cursor: "pointer" }}
      aria-label={`${ariaLabel}: ${value}`}
    >
      <Icon as={icon} boxSize={4} aria-hidden flexShrink={0} />
      <Text fontSize="sm" fontWeight="700" mb={0} lineHeight="1">
        {value}
      </Text>
    </HStack>
  );
}

const ModelList: React.FC<ModelListProps> = ({
  listModel,
  handleOpenModelEditor,
  onOpenThoughtspotFormula,
  onDeleteModel,
  statsLoading,
}) => {
  const { defaultTimeZone } = useUser();
  const [sortedModels, setSortedModels] = useState<Header[]>([]);

  return (
    <Box overflowY="auto" height="100vh" px={{ base: 3, md: 5 }} py={4}>
      <VStack align="stretch" spacing={5}>
        <HStack justify="space-between" align="center" w="full" minW={0} spacing={2} mx={1}>
          <HStack spacing={2} flexShrink={0} minW={0}>
            <Icon as={AppIcons.dataModel} boxSize={6} color="gray.700" flexShrink={0} aria-hidden />
            <Text fontSize="md" fontWeight="700" color="gray.700" letterSpacing="tight" mb={0}>
              Data models
            </Text>
          </HStack>
          <Box flex="1" minW={2} height="1px" bg="gray.300" mx={1} />
          <ConfigurationListFilterControl
            headerList={listModel}
            type="data_model"
            searchQuery=""
            splitDetailOpen={false}
            onChange={setSortedModels}
            defaultSortOption="created_desc"
          />
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
          {listModel?.length ? (
            sortedModels.map(model => {
              const typedModel = model as HeaderWithStats;
              const id = typedModel.id!;

              const displayName = typedModel.name || "Untitled model";
              const author = typedModel.author_name?.trim() || "Unknown author";
              const dateOnly = typedModel.last_modified_date_ms
                ? epochConverterWithTimeZone(typedModel.last_modified_date_ms, "d MMM YYYY", defaultTimeZone)
                : "";

              const stats = typedModel.stats as DataModelStats | undefined;
              const totalCols = stats?.total_logical_columns ?? 0;
              const numMetrics = stats?.num_mapped_metrics ?? 0;
              const numAttributes = stats?.num_mapped_attributes ?? 0;
              const numMeasures = stats?.num_mapped_measures ?? 0;
              const numTimeframes = stats?.num_mapped_timeframes ?? 0;
              // const mappedKgCount = numAttributes + numMeasures + numTimeframes;
              const numMappedLogicalColumns = stats?.num_mapped_logical_columns ?? 0;
              const progressPct =
                totalCols > 0 ? Math.min(100, Math.round((numMappedLogicalColumns / totalCols) * 100)) : 0;

              const ringTooltip =
                totalCols > 0
                  ? `Ontology progress: ${numMappedLogicalColumns} of ${totalCols} 
                  worksheet columns mapped to KG (${progressPct}%).`
                  : `${numMappedLogicalColumns} columns mapped to KG.`;

              return (
                <Card
                  key={id}
                  variant="outline"
                  borderRadius="lg"
                  boxShadow="md"
                  bg="white"
                  overflow="hidden"
                  display="flex"
                  flexDirection="column"
                  borderColor="gray.400"
                  cursor="pointer"
                  onClick={() => handleOpenModelEditor(id)}
                  _hover={{ boxShadow: "lg", borderColor: "purple.300" }}
                  transition="box-shadow 0.15s ease, border-color 0.15s ease"
                >
                  <CardBody p={3} flex="1">
                    <Flex direction="column" gap={1.5} h="100%">
                      {/* Title + actions menu */}
                      <Flex align="center" justify="space-between" gap={2} w="100%">
                        <Text
                          flex={1}
                          minW={0}
                          fontSize="md"
                          fontWeight="650"
                          color="gray.900"
                          noOfLines={2}
                          mb={0}
                          textAlign="left"
                        >
                          {displayName}
                        </Text>
                        <Menu placement="bottom-start" strategy="fixed" closeOnSelect>
                          <MenuButton
                            as={IconButton}
                            aria-label="Model actions"
                            icon={<Icon as={AppIcons.modelActions} boxSize={6} />}
                            variant="ghost"
                            size="sm"
                            color="purple.700"
                            flexShrink={0}
                            _hover={{ bg: "purple.50", borderColor: "purple.300" }}
                            _active={{ bg: "purple.100" }}
                            alignSelf="flex-start"
                            onClick={e => e.stopPropagation()}
                          />
                          <MenuList zIndex={20} fontSize="md" minW="200px">
                            <MenuItem
                              icon={<Icon as={AppIcons.mathFunction} boxSize={5} />}
                              isDisabled={statsLoading}
                              onClick={() => onOpenThoughtspotFormula(id)}
                              fontWeight="600"
                            >
                              Edit Model
                            </MenuItem>
                            <MenuItem
                              icon={<Icon as={AppIcons.analystScope} boxSize={5} />}
                              onClick={() => handleOpenModelEditor(id)}
                              fontWeight="600"
                            >
                              Map to Ontology
                            </MenuItem>
                            <MenuItem
                              icon={<Icon as={AppIcons.delete} boxSize={5} />}
                              color="red.600"
                              onClick={() => onDeleteModel(id)}
                              fontWeight="600"
                            >
                              Delete model
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Flex>

                      {/* Date + author */}
                      <Flex w="100%" justify="space-between">
                        <HStack
                          spacing={2}
                          flexWrap="wrap"
                          justify="space-between"
                          maxW="100%"
                          fontSize="xs"
                          color="gray.500"
                          fontWeight="500"
                          w="100%"
                        >
                          <HStack spacing={1} minW={0}>
                            <Icon as={AppIcons.clock} boxSize={3} color="gray.400" flexShrink={0} aria-hidden />
                            <Text noOfLines={1} mb={0}>
                              {dateOnly}
                            </Text>
                          </HStack>
                          <HStack spacing={1} minW={0}>
                            <Icon as={AppIcons.user} boxSize={3} color="gray.400" flexShrink={0} aria-hidden />
                            <Text noOfLines={1} mb={0}>
                              {author}
                            </Text>
                          </HStack>
                        </HStack>
                      </Flex>

                      {/* Progress ring */}
                      {!statsLoading ? (
                        <Flex align="center" gap={3} w="100%" py={0.5}>
                          <MappingProgressRing tooltipLabel={ringTooltip} progressPct={progressPct} />
                          <VStack align="start" spacing={0} flex={1} minW={0}>
                            <Text fontSize="xs" fontWeight="600" color="gray.700" mb={0} lineHeight="short">
                              {`${numMappedLogicalColumns} / ${totalCols} columns in ontology`}
                            </Text>
                          </VStack>
                        </Flex>
                      ) : (
                        <HStack spacing={3} align="center" py={0.5} w="100%">
                          <Skeleton width="34px" height="34px" borderRadius="full" flexShrink={0} />
                          <Skeleton height="16px" flex={1} maxW="240px" borderRadius="sm" />
                        </HStack>
                      )}

                      {/* Stat chips */}
                      <Box>
                        {statsLoading ? (
                          <HStack spacing={2} flexWrap="wrap" py={0.5}>
                            <Skeleton height="32px" width="72px" borderRadius="md" />
                            <Skeleton height="32px" width="64px" borderRadius="md" />
                            <Skeleton height="32px" width="64px" borderRadius="md" />
                          </HStack>
                        ) : (
                          <Flex w="100%" align="center" justify="space-between" gap={2} flexWrap="wrap" py={0.5}>
                            <Box flexShrink={0} minW="min-content">
                              {numMetrics > 0 && (
                                <CompactStatusChip
                                  icon={AppIcons.metric}
                                  value={numMetrics}
                                  ariaLabel="Metrics on this model"
                                />
                              )}
                            </Box>
                            <HStack spacing={2} flexWrap="wrap" justify="flex-end" flex="1" minW={0}>
                              {numAttributes > 0 && (
                                <CompactStatusChip
                                  icon={AppIcons.attribute}
                                  value={numAttributes}
                                  ariaLabel="Attribute links in knowledge graph"
                                />
                              )}
                              {numMeasures > 0 && (
                                <CompactStatusChip
                                  icon={AppIcons.measure}
                                  value={numMeasures}
                                  ariaLabel="Measure links in knowledge graph"
                                />
                              )}
                              {numTimeframes > 0 && (
                                <CompactStatusChip
                                  icon={AppIcons.timeframe}
                                  value={numTimeframes}
                                  ariaLabel="Datetime links in knowledge graph"
                                />
                              )}
                            </HStack>
                          </Flex>
                        )}
                      </Box>
                    </Flex>
                  </CardBody>
                </Card>
              );
            })
          ) : (
            <Box gridColumn="1 / -1">
              <Text color="gray.500" fontSize="sm">
                No data models yet. Create one from the ThoughtSpot flow or your admin tools.
              </Text>
            </Box>
          )}
        </SimpleGrid>
      </VStack>
    </Box>
  );
};

export default ModelList;
